import { FC, ChangeEvent, useState, useEffect } from "react"
import { RateFlowDetails } from "../../../../../../../../types"
import {
  RATEFLOW_ENGINE_CONFIGS,
  RATEFLOW_ENGINE_TEMPLATES
} from "../../../../../../../constants"
import {
  CONFIG_ADMIN_INPUT_CLASS,
  ConfigAdminPanelStackedField,
  ConfigAdminToggleRow
} from "../../../../../config-admin-form-primitives"
import { ConfigAdminPanel } from "../../../../../config-admin-panel"

const LOAN_SIFTER_DEFAULTS = RATEFLOW_ENGINE_CONFIGS.find(
  c => c.api_mode === "loan_sifter"
)?.api_config

const maskValue = (value: string): string => {
  if (!value) return ""
  const prefix = Math.min(6, value.length)
  return value.slice(0, prefix) + "•".repeat(Math.max(0, value.length - prefix))
}

interface MaskedCredentialInputProps {
  id: string
  value: string
  onChange: (value: string) => void
  className?: string
}

const MaskedCredentialInput: FC<MaskedCredentialInputProps> = ({
  id,
  value,
  onChange,
  className
}) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState("")

  return (
    <input
      id={id}
      type="text"
      value={editing ? draft : maskValue(value)}
      onChange={e => setDraft(e.target.value)}
      onFocus={() => {
        setEditing(true)
        setDraft("")
      }}
      onBlur={() => {
        if (draft) onChange(draft)
        setEditing(false)
        setDraft("")
      }}
      placeholder={editing ? "Enter new value…" : undefined}
      className={className}
    />
  )
}

interface Props {
  values: RateFlowDetails
  setFormField: (name: string, value: any) => void
}

type CustomValue = { name: string; value: string }

interface RawDefaultsPanelProps {
  hint: string
  entries: [string, string | number][]
  onUpdate: (key: string, value: string, index: number) => void
  onAdd: () => void
  onRemove: (index: number) => void
}

const RawDefaultsPanel: FC<RawDefaultsPanelProps> = ({
  hint,
  entries,
  onUpdate,
  onAdd,
  onRemove
}) => (
  <ConfigAdminPanel>
    <ConfigAdminPanelStackedField
      label="Raw Defaults"
      fieldKey="raw_defaults"
      hint={hint}
      htmlFor="raw-defaults"
      dividerBottom={false}
    >
      <div className="space-y-2 w-full max-w-xl">
        {entries.map(([k, v], i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              value={k}
              placeholder="Key"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onUpdate(e.target.value, String(v), i)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
            <input
              type="text"
              value={String(v)}
              placeholder="Value"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onUpdate(k, e.target.value, i)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="px-2 py-1 text-xs text-red-500 hover:text-red-700 shrink-0"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={onAdd}
          className="text-xs text-primary-600 hover:text-primary-800"
        >
          + Add raw default
        </button>
      </div>
    </ConfigAdminPanelStackedField>
  </ConfigAdminPanel>
)

const RateflowPricingEngine: FC<Props> = ({ values, setFormField }) => {
  // Normalize legacy "ob" to "optimal_blue" for display
  const selectedTemplate =
    values.settings?.api_mode === "ob"
      ? "optimal_blue"
      : values.settings?.api_mode || ""

  const handleSelectTemplate = (templateId: string) => {
    if (!templateId) {
      setFormField("settings", {
        ...values.settings,
        api_mode: "",
        api_config: {},
        raw_defaults: {}
      })
      return
    }
    const selectedConfig = RATEFLOW_ENGINE_CONFIGS.find(
      rec => rec.api_mode === templateId
    )
    if (selectedConfig) {
      setFormField("settings", {
        ...values.settings,
        api_mode: selectedConfig.api_mode,
        api_config: { ...selectedConfig.api_config },
        raw_defaults: {}
      })
    }
  }

  const updateApiConfig = (field: string, value: any) => {
    setFormField("settings", {
      ...values.settings,
      api_config: {
        ...(values.settings?.api_config || {}),
        [field]: value
      }
    })
  }

  const updateRawDefaults = (key: string, value: string, index: number) => {
    const entries = Object.entries(values.settings?.raw_defaults || {}) as [
      string,
      string | number
    ][]
    entries[index] = [key, value]
    setFormField("settings", {
      ...values.settings,
      raw_defaults: Object.fromEntries(entries)
    })
  }

  const addRawDefault = () => {
    setFormField("settings", {
      ...values.settings,
      raw_defaults: { ...(values.settings?.raw_defaults || {}), "": "" }
    })
  }

  const removeRawDefault = (index: number) => {
    const entries = Object.entries(values.settings?.raw_defaults || {}) as [
      string,
      string | number
    ][]
    entries.splice(index, 1)
    setFormField("settings", {
      ...values.settings,
      raw_defaults: Object.fromEntries(entries)
    })
  }

  const apiConfig = values.settings?.api_config || {}

  useEffect(() => {
    if (selectedTemplate !== "loan_sifter" || !LOAN_SIFTER_DEFAULTS) return
    const current = values.settings?.api_config || {}
    if (current.client_id && current.client_secret) return

    setFormField("settings", {
      ...values.settings,
      api_config: {
        ...current,
        client_id: current.client_id || LOAN_SIFTER_DEFAULTS.client_id,
        client_secret:
          current.client_secret || LOAN_SIFTER_DEFAULTS.client_secret
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate])

  const customValues = (apiConfig.custom_values as CustomValue[]) || []
  const rawDefaultEntries = Object.entries(
    values.settings?.raw_defaults || {}
  ) as [string, string | number][]

  return (
    <div className="space-y-6 pt-2">
      {/* Engine selector */}
      <ConfigAdminPanel>
        <ConfigAdminPanelStackedField
          label="API Mode"
          fieldKey="api_mode"
          hint="Required - Name of the pricing engine being used"
          htmlFor="rateflow-api-mode"
          dividerBottom={false}
        >
          <select
            id="rateflow-api-mode"
            value={selectedTemplate}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              handleSelectTemplate(e.target.value)
            }
            className={CONFIG_ADMIN_INPUT_CLASS}
          >
            <option value="">Select API mode...</option>
            {RATEFLOW_ENGINE_TEMPLATES.map(template => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </ConfigAdminPanelStackedField>
      </ConfigAdminPanel>

      {/* API Configuration - Polly */}
      {selectedTemplate === "polly" && (
        <ConfigAdminPanel>
          <h3 className="text-sm font-medium text-gray-700 px-4 pt-3 pb-2">
            Polly API Configuration
          </h3>
          <ConfigAdminPanelStackedField
            label="Audience ID"
            fieldKey="audience_id"
            hint="Identifier for the audience scope used in authentication"
            htmlFor="polly-audience-id"
          >
            <input
              id="polly-audience-id"
              type="text"
              value={apiConfig.audience_id || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("audience_id", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="API Username"
            fieldKey="api_username"
            hint="Username used for authenticating with the pricing engine"
            htmlFor="polly-api-username"
          >
            <input
              id="polly-api-username"
              type="text"
              value={apiConfig.api_username || "BankingBridgeAPI"}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("api_username", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="API Password"
            fieldKey="api_password"
            htmlFor="polly-api-password"
          >
            <input
              id="polly-api-password"
              type="password"
              value={apiConfig.api_password || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("api_password", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Client ID"
            fieldKey="client_id"
            hint="Engine-specific client identifier"
            htmlFor="polly-client-id"
          >
            <input
              id="polly-client-id"
              type="text"
              value={apiConfig.client_id || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("client_id", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Client Secret"
            fieldKey="client_secret"
            hint="Secret key paired with the client ID"
            htmlFor="polly-client-secret"
          >
            <input
              id="polly-client-secret"
              type="password"
              value={apiConfig.client_secret || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("client_secret", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminToggleRow
            label="Include Fees"
            description="Whether to include fees in the pricing response"
            enabled={apiConfig.include_fees || false}
            onChange={(value: boolean) =>
              updateApiConfig("include_fees", value)
            }
          />

          <ConfigAdminPanelStackedField
            label="Custom Values"
            fieldKey="custom_values"
            hint="Additional key-value pairs passed to the Polly pricing engine"
            htmlFor="polly-custom-values"
            dividerBottom={false}
          >
            <div className="space-y-2 w-full max-w-xl">
              {customValues.map((cv, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    value={cv.name}
                    placeholder="Name"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const updated = [...customValues]
                      updated[i] = { ...updated[i], name: e.target.value }
                      updateApiConfig("custom_values", updated)
                    }}
                    className={CONFIG_ADMIN_INPUT_CLASS}
                  />
                  <input
                    type="text"
                    value={cv.value}
                    placeholder="Value"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const updated = [...customValues]
                      updated[i] = { ...updated[i], value: e.target.value }
                      updateApiConfig("custom_values", updated)
                    }}
                    className={CONFIG_ADMIN_INPUT_CLASS}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      updateApiConfig(
                        "custom_values",
                        customValues.filter((_, idx) => idx !== i)
                      )
                    }
                    className="px-2 py-1 text-xs text-red-500 hover:text-red-700 shrink-0"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  updateApiConfig("custom_values", [
                    ...customValues,
                    { name: "", value: "" }
                  ])
                }
                className="text-xs text-primary-600 hover:text-primary-800"
              >
                + Add custom value
              </button>
            </div>
          </ConfigAdminPanelStackedField>
        </ConfigAdminPanel>
      )}

      {/* API Configuration - Mortech */}
      {selectedTemplate === "mortech" && (
        <ConfigAdminPanel>
          <h3 className="text-sm font-medium text-gray-700 px-4 pt-3 pb-2">
            Mortech API Configuration
          </h3>
          <ConfigAdminPanelStackedField
            label="Customer ID"
            fieldKey="customer_id"
            hint="Identifier for the lender or account within the pricing engine"
            htmlFor="mortech-customer-id"
          >
            <input
              id="mortech-customer-id"
              type="text"
              value={apiConfig.customer_id || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("customer_id", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Lead Source"
            fieldKey="lead_source"
            hint="Lead source identifier for Mortech pricing requests"
            htmlFor="mortech-lead-source"
          >
            <input
              id="mortech-lead-source"
              type="number"
              value={apiConfig.lead_source ?? ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig(
                  "lead_source",
                  e.target.value === "" ? null : parseInt(e.target.value, 10)
                )
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Email"
            fieldKey="email"
            htmlFor="mortech-email"
            dividerBottom={false}
          >
            <input
              id="mortech-email"
              type="email"
              value={apiConfig.email || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("email", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>
        </ConfigAdminPanel>
      )}

      {/* API Configuration - Lender Price */}
      {selectedTemplate === "lender_price" && (
        <ConfigAdminPanel>
          <h3 className="text-sm font-medium text-gray-700 px-4 pt-3 pb-2">
            Lender Price API Configuration
          </h3>
          <ConfigAdminPanelStackedField
            label="User ID"
            fieldKey="user_id"
            hint="Unique user identifier used in pricing engine configurations"
            htmlFor="lender-price-user-id"
          >
            <input
              id="lender-price-user-id"
              type="text"
              value={apiConfig.user_id || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("user_id", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Company ID"
            fieldKey="company_id"
            hint="Identifier for the lender or account within the pricing engine"
            htmlFor="lender-price-company-id"
          >
            <input
              id="lender-price-company-id"
              type="text"
              value={apiConfig.company_id || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("company_id", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="API Key"
            fieldKey="api_key"
            hint="API key issued by the pricing engine for secure requests"
            htmlFor="lender-price-api-key"
            dividerBottom={false}
          >
            <input
              id="lender-price-api-key"
              type="password"
              value={apiConfig.api_key || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("api_key", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>
        </ConfigAdminPanel>
      )}

      {/* Raw Defaults - Lender Price */}
      {selectedTemplate === "lender_price" && (
        <RawDefaultsPanel
          hint="Key-value pairs passed directly to the Lender Price pricing request (e.g. criteria.compensationType)"
          entries={rawDefaultEntries}
          onUpdate={updateRawDefaults}
          onAdd={addRawDefault}
          onRemove={removeRawDefault}
        />
      )}

      {/* API Configuration - Loan Sifter */}
      {selectedTemplate === "loan_sifter" && (
        <ConfigAdminPanel>
          <h3 className="text-sm font-medium text-gray-700 px-4 pt-3 pb-2">
            Loan Sifter API Configuration
          </h3>
          <ConfigAdminPanelStackedField
            label="User ID"
            fieldKey="user_id"
            hint="Unique user identifier used in pricing engine configurations"
            htmlFor="loan-sifter-user-id"
          >
            <input
              id="loan-sifter-user-id"
              type="text"
              value={apiConfig.user_id || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("user_id", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Company ID"
            fieldKey="company_id"
            hint="Identifier for the lender or account within the pricing engine"
            htmlFor="loan-sifter-company-id"
          >
            <input
              id="loan-sifter-company-id"
              type="text"
              value={apiConfig.company_id || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("company_id", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Client ID"
            fieldKey="client_id"
            hint="Hardcoded for Loan Sifter. Click to replace."
            htmlFor="loan-sifter-client-id"
          >
            <MaskedCredentialInput
              id="loan-sifter-client-id"
              value={apiConfig.client_id || ""}
              onChange={value => updateApiConfig("client_id", value)}
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Client Secret"
            fieldKey="client_secret"
            hint="Hardcoded for Loan Sifter. Click to replace."
            htmlFor="loan-sifter-client-secret"
          >
            <MaskedCredentialInput
              id="loan-sifter-client-secret"
              value={apiConfig.client_secret || ""}
              onChange={value => updateApiConfig("client_secret", value)}
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Fee Engine"
            fieldKey="fee_engine"
            hint="Fee calculation engine to use (e.g., lodestar)"
            htmlFor="loan-sifter-fee-engine"
            dividerBottom={false}
          >
            <input
              id="loan-sifter-fee-engine"
              type="text"
              value={apiConfig.fee_engine || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("fee_engine", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>
        </ConfigAdminPanel>
      )}

      {/* Raw Defaults - Loan Sifter */}
      {selectedTemplate === "loan_sifter" && (
        <RawDefaultsPanel
          hint="Key-value pairs passed directly to the Loan Sifter pricing request (e.g. productCharacteristics.desiredPrice)"
          entries={rawDefaultEntries}
          onUpdate={updateRawDefaults}
          onAdd={addRawDefault}
          onRemove={removeRawDefault}
        />
      )}

      {/* API Configuration - Optimal Blue */}
      {selectedTemplate === "optimal_blue" && (
        <ConfigAdminPanel>
          <h3 className="text-sm font-medium text-gray-700 px-4 pt-3 pb-2">
            Optimal Blue API Configuration
          </h3>
          <ConfigAdminPanelStackedField
            label="Originator ID"
            fieldKey="originator_id"
            hint="Unique originator identifier within Optimal Blue"
            htmlFor="ob-originator-id"
          >
            <input
              id="ob-originator-id"
              type="text"
              value={apiConfig.originator_id || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("originator_id", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Business Channel"
            fieldKey="biz_channel"
            hint="Business channel identifier used in Optimal Blue pricing requests"
            htmlFor="ob-biz-channel"
          >
            <input
              id="ob-biz-channel"
              type="text"
              value={apiConfig.biz_channel || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("biz_channel", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminPanelStackedField
            label="Fee Engine"
            fieldKey="fee_engine"
            hint="Fee calculation engine to use (e.g., lodestar)"
            htmlFor="ob-fee-engine"
          >
            <input
              id="ob-fee-engine"
              type="text"
              value={apiConfig.fee_engine || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateApiConfig("fee_engine", e.target.value)
              }
              className={CONFIG_ADMIN_INPUT_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminToggleRow
            label="Group Requests"
            description="Whether to group multiple pricing requests together"
            enabled={apiConfig.group_requests || false}
            onChange={(value: boolean) =>
              updateApiConfig("group_requests", value)
            }
            last
          />
        </ConfigAdminPanel>
      )}
    </div>
  )
}

export default RateflowPricingEngine
